import React, { useState } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Share2, Copy, Check, Linkedin, Twitter, Facebook, Mail,
  ExternalLink, Download, Loader2, QrCode, Eye, Sparkles, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { absoluteUrl } from '../../lib/siteConfig';
import { FeatureGate } from './TierComponents';
import { ReserveProSpotModal } from './ReserveProSpotModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable share control — copy link, native share, social links, and a
 * downloadable QR code (fetched from the backend, encodes the public profile URL).
 *
 * Props: url (string), title, clinicianId (for QR), triggerLabel, triggerClassName, testId
 */
export const ShareProfile = ({
  url,
  title = 'Check out this verified clinician on CaraLoom',
  clinicianId,
  triggerLabel = 'Share',
  triggerClassName = '',
  testId = 'share-profile',
}) => {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const shareText = `${title} — ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: title, url }); } catch (e) { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const loadQr = async () => {
    if (qr || !clinicianId) return;
    setQrLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/clinicians/${clinicianId}/qr`);
      setQr(data.qr);
    } catch (e) { /* swallow */ } finally {
      setQrLoading(false);
    }
  };

  const socials = [
    { Icon: Twitter, label: 'X', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}` },
    { Icon: Linkedin, label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
    { Icon: Facebook, label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { Icon: Mail, label: 'Email', href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}` },
  ];

  return (
    <Popover onOpenChange={(o) => o && loadQr()}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClassName || 'inline-flex items-center gap-1.5 cn-btn cn-btn-ghost !py-2'}
          data-testid={`${testId}-trigger`}
        >
          <Share2 className="w-4 h-4" /> {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 bg-white dark:bg-cn-ink-800 border-cn-border" data-testid={`${testId}-panel`}>
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-cn-text-muted">Share this profile</p>

        {/* Copy link */}
        <button
          type="button"
          onClick={copy}
          data-testid={`${testId}-copy`}
          className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-cn-border hover:border-cn-primary/40 hover:bg-cn-primary/5 transition-colors text-left"
        >
          {copied ? <Check className="w-4 h-4 text-cn-success" /> : <Copy className="w-4 h-4 text-cn-primary" />}
          <span className="text-sm text-cn-text dark:text-white truncate flex-1">{url}</span>
        </button>

        {/* Native share */}
        <button
          type="button"
          onClick={nativeShare}
          data-testid={`${testId}-native`}
          className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-cn-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <Share2 className="w-4 h-4" /> Share via…
        </button>

        {/* Social row */}
        <div className="mt-3 grid grid-cols-4 gap-2">
          {socials.map(({ Icon, label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={`Share on ${label}`}
              data-testid={`${testId}-social-${label.toLowerCase()}`}
              className="flex items-center justify-center py-2 rounded-xl border border-cn-border hover:border-cn-primary/40 hover:bg-cn-primary/5 text-cn-text-muted hover:text-cn-primary transition-colors"
            >
              <Icon className="w-4 h-4" />
            </a>
          ))}
        </div>

        {/* QR */}
        {clinicianId && (
          <div className="mt-4 pt-3 border-t border-cn-border/60">
            <div className="flex items-center gap-1.5 mb-2">
              <QrCode className="w-3.5 h-3.5 text-cn-primary" />
              <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Scan or print</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-xl border border-cn-border bg-white flex items-center justify-center overflow-hidden">
                {qrLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-cn-primary" />
                ) : qr ? (
                  <img src={qr} alt="Profile QR code" className="w-full h-full object-contain" data-testid={`${testId}-qr-img`} />
                ) : (
                  <QrCode className="w-6 h-6 text-cn-text-muted" />
                )}
              </div>
              {qr && (
                <a
                  href={qr}
                  download="caraloom-profile-qr.png"
                  data-testid={`${testId}-qr-download`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-cn-primary hover:gap-2.5 transition-all"
                >
                  <Download className="w-4 h-4" /> Download QR
                </a>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Clinician dashboard card — surfaces the clinician's own shareable public
 * profile link with copy / view / share / QR. Renders inner content only
 * (wrap in a BentoTile / cn-card by the caller).
 */
export const ClinicianShareCard = ({ profile }) => {
  const [copied, setCopied] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const slug = profile?.slug;
  const shareUrl = profile?.public_url || (slug ? absoluteUrl(`/clinicians/${slug}`) : null);
  const viewHref = slug ? `/clinicians/${slug}` : null;
  // Pro white-label vanity URL: `caraloom.com/<slug>` (vs the default `/clinicians/<slug>`).
  const vanityUrl = slug ? absoluteUrl(`/${slug}`) : null;

  if (!shareUrl) {
    return (
      <div data-testid="clinician-share-card">
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-cn-text-muted">Grow your practice</p>
        <h3 className="font-display font-bold text-lg text-cn-text dark:text-white">Your shareable profile</h3>
        <p className="text-sm text-cn-text-muted mt-2">Complete your practice profile to unlock your public, shareable link.</p>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Profile link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  return (
    <div data-testid="clinician-share-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-cn-text-muted">Grow your practice</p>
          <h3 className="font-display font-bold text-lg text-cn-text dark:text-white leading-tight">Your shareable profile</h3>
          <p className="text-sm text-cn-text-muted mt-1">Win more bookings — share your verified CaraLoom profile on social, in your email signature, or print the QR for cards.</p>
        </div>
        <ShareProfile
          url={shareUrl}
          clinicianId={profile?.user_id}
          title="My verified clinician profile on CaraLoom"
          testId="clinician-share"
          triggerClassName="inline-flex items-center gap-1.5 cn-btn cn-btn-primary !py-2 shrink-0"
        />
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm" data-testid="clinician-view-stats">
        <Eye className="w-4 h-4 text-cn-primary shrink-0" />
        {profile?.views_total > 0 ? (
          <span className="text-cn-text-muted">
            <span className="font-semibold text-cn-text dark:text-white">{profile.views_total}</span> profile {profile.views_total === 1 ? 'view' : 'views'}
            {profile.views_this_week > 0 && (
              <> · <span className="font-semibold text-cn-primary">{profile.views_this_week}</span> this week</>
            )}
          </span>
        ) : (
          <span className="text-cn-text-muted">No views yet — share your link to get discovered by families.</span>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-cn-bg/70 border border-cn-border/60">
        <span className="text-sm font-mono text-cn-text dark:text-white truncate flex-1" data-testid="clinician-share-url">{shareUrl}</span>
        <button
          type="button"
          onClick={copy}
          data-testid="clinician-share-copy"
          aria-label="Copy profile link"
          className="p-2 rounded-xl text-cn-primary hover:bg-cn-primary/10 transition-colors shrink-0"
        >
          {copied ? <Check className="w-4 h-4 text-cn-success" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>

      <a
        href={viewHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="clinician-view-public-profile"
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-cn-primary hover:gap-2.5 transition-all"
      >
        Preview public profile <ExternalLink className="w-4 h-4" />
      </a>

      {/* Pro: white-label vanity URL (caraloom.com/<your-slug>) */}
      {slug && (
        <FeatureGate
          feature="white_label_url"
          fallback={
            <div
              className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-dashed border-cn-primary/30 bg-cn-primary/5"
              data-testid="white-label-locked"
            >
              <Lock className="w-4 h-4 text-cn-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-primary">Pro · Personal vanity URL</p>
                <p className="text-xs text-cn-text-muted truncate">
                  Unlock <span className="font-mono">{vanityUrl?.replace(/^https?:\/\//, '')}</span> — shorter, brandable, easier to share.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReserveOpen(true)}
                className="cn-btn cn-btn-ghost !py-1.5 !px-3 !text-xs whitespace-nowrap"
                data-testid="white-label-upgrade-btn"
              >
                <Sparkles className="w-3 h-3" /> Reserve
              </button>
            </div>
          }
        >
          <div
            className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-gradient-to-r from-cn-primary/10 to-cn-accent/5 border border-cn-primary/25"
            data-testid="white-label-active"
          >
            <Sparkles className="w-4 h-4 text-cn-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-primary">Pro · Your vanity URL</p>
              <p className="text-sm font-mono text-cn-text dark:text-white truncate" data-testid="white-label-url">{vanityUrl}</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(vanityUrl);
                  toast.success('Vanity URL copied');
                } catch (e) { toast.error('Could not copy'); }
              }}
              className="p-2 rounded-xl text-cn-primary hover:bg-cn-primary/10 transition-colors shrink-0"
              aria-label="Copy vanity URL"
              data-testid="white-label-copy"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </FeatureGate>
      )}

      <ReserveProSpotModal
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        plan={{ id: 'clinician.pro', name: 'Pro', audience: 'clinician', auto_upgrade_threshold_usd: 2000 }}
      />
    </div>
  );
};

export default ShareProfile;
